import { useState } from 'react';
import { ApiError, createEstablishment, type CreatedEstablishment } from '../lib/api.js';
import { describeFailure } from '../lib/failure.js';
import { ConsentBanner } from '../components/ConsentBanner.js';
import { LegalFooter } from '../components/LegalFooter.js';
import { Alert, Button, Input, Logo } from '../design/index.js';

/**
 * The first screen of a salon that signed itself up.
 *
 * Somebody created an account and signed in, and belongs to no establishment
 * yet. Before, they read "ask for an invitation" and had nowhere to go; now
 * they name their salon, give its address, and become its owner.
 *
 * ## What it deliberately does not ask
 *
 * - **The time zone.** The server derives it from the postal code. A list of
 *   zones is where a salon in Guadeloupe picks Paris by mistake, and a wrong
 *   zone moves every slot the assistant offers without anything saying so.
 * - **An e-mail address.** The owner's address is the one the identity
 *   provider stated at sign-in. A field here would be a second, unverified
 *   answer to the same question.
 * - **A plan or a card.** One decision per screen: those come next, once the
 *   establishment exists to be subscribed.
 */
export function CreateEstablishment({
  baseUrl,
  token,
  onCreated,
}: {
  readonly baseUrl: string;
  readonly token: string;
  readonly onCreated: (created: CreatedEstablishment) => void;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete =
    name.trim().length >= 2 && /^\d{2}\s?\d{3}$/.test(postalCode.trim()) && city.trim() !== '';

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const created = await createEstablishment(
        { baseUrl, token },
        {
          name: name.trim(),
          postalCode: postalCode.trim(),
          city: city.trim(),
          ...(addressLine.trim() === '' ? {} : { addressLine: addressLine.trim() }),
        },
      );
      onCreated(created);
    } catch (cause: unknown) {
      setError(messageFor(cause));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <Logo tone="light" />
      <h1 className="mt-8 text-heading font-bold text-ink">Créez votre établissement</h1>
      <p className="mt-2 text-ui-sm text-ink-muted">
        Trois informations, et votre espace est ouvert. Vous choisirez ensuite votre offre, puis
        vous installerez l’assistante à votre rythme.
      </p>

      <form
        className="mt-8 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !busy) void submit();
        }}
      >
        {error !== null && (
          <Alert kind="error" title="L’établissement n’a pas été créé">
            {error}
          </Alert>
        )}

        <Input
          label="Nom de l’établissement"
          hint="Celui que vos clients connaissent. L’assistante le prononcera au téléphone."
          value={name}
          placeholder="QG Haircut"
          autoComplete="organization"
          maxLength={120}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />

        <Input
          label="Adresse"
          hint="Facultative."
          value={addressLine}
          placeholder="12 rue des Flamboyants"
          autoComplete="street-address"
          maxLength={200}
          onChange={(event) => {
            setAddressLine(event.target.value);
          }}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Code postal"
            hint="Il fixe l’heure de votre agenda."
            value={postalCode}
            placeholder="97170"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={6}
            onChange={(event) => {
              setPostalCode(event.target.value);
            }}
          />
          <Input
            label="Ville"
            value={city}
            placeholder="Petit-Bourg"
            autoComplete="address-level2"
            maxLength={100}
            onChange={(event) => {
              setCity(event.target.value);
            }}
          />
        </div>

        <Button type="submit" variant="primary" block disabled={!complete || busy}>
          {busy ? 'Création en cours…' : 'Créer mon établissement'}
        </Button>
      </form>

      <p className="mt-8 text-ui-sm text-ink-muted">
        Vous rejoignez un salon qui utilise déjà GSI Voice ? Ouvrez le lien d’invitation reçu par
        courriel, depuis ce navigateur : il vous rattache à son équipe.
      </p>

      <LegalFooter />
      <ConsentBanner />
    </div>
  );
}

/** The server's code, in the words of the person who just pressed the button. */
function messageFor(cause: unknown): string {
  // No answer is not a refusal: the establishment may exist, and « réessayez »
  // would create a second one. `describeFailure` says to reload and look.
  if (!(cause instanceof ApiError)) return describeFailure(cause);
  switch (cause.code) {
    // Written in French on the server, for exactly this screen.
    case 'signup.territory_unsupported':
    case 'signup.too_many_unpaid':
      return cause.message;
    case 'signup.email_unknown':
      return 'Votre compte de connexion ne nous a pas transmis d’adresse e-mail. Déconnectez-vous, reconnectez-vous, puis réessayez ; si cela se reproduit, écrivez-nous.';
    case 'validation.failed':
      return 'Vérifiez le nom, le code postal (cinq chiffres) et la ville.';
    case 'rate_limited':
      return 'Trop de tentatives en peu de temps. Patientez quelques minutes avant de réessayer.';
    case 'authn.required':
      return 'Votre session a expiré. Reconnectez-vous pour continuer.';
    default:
      return describeFailure(cause);
  }
}
